import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../css/main.scss";
import Header from "./common/Header";
import Footer from "./common/Footer";
import { useTranslation } from "react-i18next";
import { get_product_list } from "../apis/GetProductList";
import { get_category_list } from "../apis/GetCategoryList";
import { BASE_URL } from "../constans/ApiEndpointConstants";
import { sendMessage } from "../apis/SendMessageAPI";
import LocalStorageUtil from "../utils/localStorageUtil";
import { useNavigate } from "react-router-dom";
import {  isGbpLanguage } from "../constans/CurrencyConstants";
import { telegram } from "../constans/ColorConstants";
import {
  pickDosageLine,
  pickMinPriceNumber,
  pickProductTitle,
} from "../utils/productCardHelpers";

const Main = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [desktopPage, setDesktopPage] = useState(0); // 商品分页（桌面每页 4 个 = 一行四个；移动端同页 4 个 = 2×2）
  const [catCounts, setCatCounts] = useState({}); // 各分类下商品数量 { [categoryId 字符串]: count }
  const [categoriesData, setCategoriesData] = useState([]);
  const [productRawList, setProductRawList] = useState([]); // 接口原始列表（含 name_nl / name_uk）
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false); // 商品列表加载中状态

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
 
  const storedAd = LocalStorageUtil.getAd();
  const ad = storedAd || "PurityBase";

  useEffect(() => {
    const userid = localStorage.getItem("userid");
    const message = `${BASE_URL} [Main page]:ad=${ad}:userid=${userid}`;
    sendMessage(message);
  }, [ad]);

  //分类
  const categories = [
    { key: "fat_burning", id: 9, icon: "main2-1.png" },
    { key: "muscle_building", id: 10, icon: "main2-2.png" },
    { key: "serums", id: 8, icon: "main2-3.png" },
    { key: "mental_focus", id: 14, icon: "main2-4.png" },
    { key: "hormonal_support", id: 13, icon: "main2-5.png" },
    { key: "recovery", id: 11, icon: "main2-6.png" },
    { key: "cosmetic", id: 12, icon: "main2-7.png" },
    { key: "supplies", id: 15, icon: "main2-8.png" },
  ];

  /**
   * 进入主页时一次性获取全量商品列表，统计各分类商品数量
   * 存入 catCounts，供分类卡片展示数量使用
   */
  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          get_category_list(1, 100),
          get_product_list(1, 1000, null)
        ]);
        const data = prodRes?.data;
        const list = data?.data?.list || data?.list || data?.data?.items || [];
        if (!alive) return;
        const map = {};
        (Array.isArray(list) ? list : []).forEach((it) => {
          const cid = String(it?.categoryid ?? it?.category_id ?? "");
          if (!cid) return;
          map[cid] = (map[cid] || 0) + 1;
        });
        setCatCounts(map);

        const catData = catRes?.data;
        const catList = catData?.data?.list || catData?.list || catData?.data?.items || [];
        const mappedCats = Array.isArray(catList) ? catList.map(it => {
          return {
            id: String(it?.id ?? it?.categoryid ?? ""),
            name_uk: it?.name_uk ?? it?.categoryname_uk ?? "",
            name_nl: it?.name_nl ?? it?.categoryname_nl ?? "",
            icon: it?.icon ?? ""
          };
        }) : [];
        setCategoriesData(mappedCats.filter(c => c.id));
      } catch {
        /* ignore */
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, []);

  /**
   * 金额格式化
   * nl 语言 → EUR (nl-NL)
   * uk/en 语言 → EUR (en-GB)
   * 全部使用欧元展示
   * @param {number} n - 要格式化的数值（欧元）
   * @returns {string} 本地化货币字符串，如 "€19,99" 或 "€17.19"
   */
  const formatEuro = useMemo(() => {
    return (n) => {
      const isUk = isGbpLanguage(i18n.language);
      const locale = isUk ? "en-GB" : "nl-NL";
      const currency = "EUR";
      const amount = n;
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
        .format(amount)
        .replace(/\u00A0/g, " ");
    };
  }, [i18n.language]);

  const langSuffix = useMemo(
    () => (i18n.language && i18n.language.toLowerCase() === "nl" ? "nl" : "uk"),
    [i18n.language]
  );

  /**
   * 将相对路径补全为绝对 URL
   * 若 src 已是 http(s):// 开头或 // 开头则直接返回
   * 否则拼接 BASE_URL
   * @param {string|null} src - 原始路径
   * @returns {string|null} 完整 URL
   */
  const withBase = useCallback((src) => {
    if (!src) return null;
    if (/^https?:\/\//i.test(src) || src.startsWith("//")) return src;
    return `${BASE_URL}${src.startsWith("/") ? src : `/${src}`}`;
  }, []);

  /**
   * 从商品原始数据中按优先级提取第一张图片 URL
   * 优先 productlink，再依次尝试 cover、image、img、thumbnail、thumb、images[0]
   * @param {object} it - 商品原始数据
   * @returns {string} 图片完整 URL，无有效图片时返回占位图
   */
  const pickImage = useCallback(
    (it) => {
      let pl = null;
      if (Array.isArray(it?.productlink) && it.productlink.length > 0) {
        const first = it.productlink[0];
        if (typeof first === "string") {
          pl = first;
        } else if (first && typeof first === "object") {
          pl = first.url || first.path || first.fileurl || first.link || null;
        }
      }
      const candidates = [
        pl,
        it?.cover,
        it?.image,
        it?.img,
        it?.thumbnail,
        it?.thumb,
        Array.isArray(it?.images) ? it.images[0] : null,
      ].filter(Boolean);
      const chosen = candidates[0];
      return withBase(chosen) || "/main/main1.avif";
    },
    [withBase]
  );

  /**
   * 从商品数据中提取并格式化价格区间字符串
   * 支持 min/max 范围价，也支持单一价格字段
   * @param {object} it - 商品原始数据
   * @returns {string} 格式化后的价格，如 "€9.99 - €19.99"
   */
  const formatPriceRange = useCallback(
    (it) => {
      const fmt = (x) => {
        if (x == null || x === "") return null;
        const num = Number(x);
        if (!isNaN(num)) return formatEuro(num);
        return `${x}`;
      };
      const min =
        it?.price_min ?? it?.min_price ?? it?.minPrice ?? it?.lowest_price;
      const max =
        it?.price_max ?? it?.max_price ?? it?.maxPrice ?? it?.highest_price;
      const single =
        it?.price ?? it?.sale_price ?? it?.priceNew ?? it?.price_new;
      if (min != null || max != null) {
        if (min != null && max != null && Number(min) !== Number(max)) {
          return `${fmt(min)} - ${fmt(max)}`;
        }
        return fmt(min ?? max) || "";
      }
      return fmt(single) || "";
    },
    [formatEuro]
  );

  const products = useMemo(
    () =>
      productRawList.map((it) => {
        const subtitle = pickDosageLine(it, langSuffix);
        const minN = pickMinPriceNumber(it, langSuffix);
        const priceLine =
          minN != null
            ? t("main.priceFrom", { price: formatEuro(minN) })
            : formatPriceRange(it) || "";
        return {
          id: it?.id || it?.product_id || it?.pid || it?._id,
          title: pickProductTitle(it, langSuffix),
          subtitle,
          priceLine,
          image: pickImage(it),
          _raw: it,
        };
      }),
    [productRawList, langSuffix, formatPriceRange, pickImage, formatEuro, t]
  );

  const reasonsParagraphs = useMemo(() => {
    const raw = t("main.reasonsText", { returnObjects: true });
    if (!Array.isArray(raw)) return [];
    return raw.map((s) => String(s ?? "").trim()).filter(Boolean);
  }, [t]);

  /**
   * 跳转到商品详情页
   * URL 格式：/shopDetails/{slug}_{id}
   * slug 由标题转小写、非字母数字替换为连字符生成
   * @param {string|number} id    - 商品 ID
   * @param {string}        title - 商品标题
   */
  const navigateToDetail = (id, title) => {
    const slug =
      (title || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, "-")
        .replace(/^-+|-+$/g, "") || "product";
    const path = id ? `${slug}_${id}` : slug;
    navigate(`/shopDetails/${encodeURIComponent(path)}`);
  };

  /**
   * 进入主页时一次性获取全量商品列表，统计各分类商品数量
   * 存入 catCounts，供分类卡片展示数量使用
   */
  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoadingCategories(true);
      try {
        const res = await get_product_list(1, 1000, null);
        const data = res?.data;
        const list = data?.data?.list || data?.list || data?.data?.items || [];
        if (!alive) return;
        const map = {};
        (Array.isArray(list) ? list : []).forEach((it) => {
          const cid = String(it?.categoryid ?? it?.category_id ?? "");
          if (!cid) return;
          map[cid] = (map[cid] || 0) + 1;
        });
        setCatCounts(map);
      } catch {
        /* ignore */
      } finally {
        if (alive) setLoadingCategories(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
  }, []);

  /**
   * 获取首页推荐商品（最多 12 条），用于轮播和网格展示
   */
  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoadingProducts(true);
      try {
        const res = await get_product_list(1, 12, null);
        const data = res?.data;
        const list =
          data?.data?.list ||
          data?.data?.items ||
          data?.list ||
          data?.rows ||
          [];
        if (!alive) return;
        if (Array.isArray(list) && list.length > 0) {
          setProductRawList(list);
        } else {
          setProductRawList([]);
        }
      } catch {
        if (alive) setProductRawList([]);
      } finally {
        if (alive) setLoadingProducts(false);
      }
    };
    run();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // i18n 翻译获取 — 顶部滚动提示条内容
  const marqueeItems = t("main.marquee", { returnObjects: true }) || [];
  const marqueeList = Array.isArray(marqueeItems) ? marqueeItems : [];
  /** 每组内重复多轮文案，保证轨道宽度 ≥ 视口，避免右侧露空后再接上 */
  const MARQUEE_CYCLES_PER_GROUP = 12;

  return (
    <div>
      <Header />
      <main>
        <section className="hero" aria-label={t("main.aria.hero")}>
          <div className="hero__content">
            <h1 className="hero__title">
              <b className="hero__titleLine">{t("main.hero.title_white")}</b>
              <span className="hero__titleAccentBlock">
                <b className="hero__titleLine hero__titleLine--yellow">
                  {t("main.hero.title_yellow")}
                </b>
                <span className="hero__titleUnderline" aria-hidden="true" />
              </span>
            </h1>
            <a className="hero__cta" href="/shop">
              {t("main.hero.cta")}
            </a>
            <a
              href={telegram}
              target="_blank"
              rel="noopener noreferrer"
              className="hero__cta_telegram"
            >
              <span className="hero__cta_telegram_iconWrap" aria-hidden="true">
                <svg className="w-7 h-7 shrink-0" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                </svg>
              </span>
              <span className="hero__cta_telegram_flex">
                <span className="hero__cta_telegram_title">
                  {t("main.hero.telegram_title")}
                </span>
                <span className="hero__cta_telegram_text">
                  {t("main.hero.telegram_text")}
                </span>
              </span>
            </a>
          </div>
        </section>

        {marqueeList.length > 0 ? (
          <div className="marquee" aria-label="Highlight strip">
            <div className="marquee__track">
              {/* 两组完全相同的轨道，动画 translate -50% 实现首尾无缝相接 */}
              {[0, 1].map((group) => (
                <div
                  className="marquee__group"
                  key={group}
                  aria-hidden={group === 1 ? true : undefined}
                >
                  {Array.from(
                    { length: MARQUEE_CYCLES_PER_GROUP },
                    (_, cycle) =>
                      marqueeList.map((text, idx) => (
                        <div
                          className="marquee__item"
                          key={`g${group}-c${cycle}-i${idx}`}
                        >
                          <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M5 16L3 6l6 4 3-6 3 6 6-4-2 10H5zm0 2h14v2H5v-2z" />
                          </svg>
                          {text}
                        </div>
                      ))
                  ).flat()}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <section className="categories" aria-label={t("main.aria.categories")}>
          <div className="categories__container">
            <h2 className="categories__title">
              <span className="categories__titleLight">
                {t("main.categoriesTitleLight")}
              </span>{" "}
              <span className="categories__titleAccent">
                {t("main.categoriesTitleAccent")}
              </span>
            </h2>
            {loadingCategories ? (
              <div className="products-loading">
                <div className="spinner" />
              </div>
            ) : (
              <div className="categories__grid">
                {categoriesData.map((c, i) => ( 
                  <a
                    className="category-card"
                    href="#"
                    key={`cat-${c.id}-${i}`}
                    onClick={(e) => {
                      e.preventDefault();
                      const kw = c.id;
                      navigate(
                        kw ? `/shop?keyword=${encodeURIComponent(kw)}` : `/shop`
                      );
                      toTop();
                    }}
                  >
                    <div className="category-card__icon" dangerouslySetInnerHTML={{__html: c.icon}} />
                    <div className="category-card__content">
                      <div className="category-card__label">
                        {langSuffix === "nl" && c.name_nl ? c.name_nl : c.name_uk}
                      </div>
                      <span className="category-card__count">
                        ({catCounts[c.id] ?? 0})
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="products" aria-label={t("main.aria.newPeptides")}>
          <div className="products__container">
            <div className="products__header">
              <h2 className="products__title">
                <span className="products__titleLight">
                  {t("main.productsTitleLight")}
                </span>
                <span className="products__titleAccent">
                  {t("main.productsTitleAccent")}
                </span>
              </h2>
            </div>
            <div className="products__header1">
              <p className="products__text">{t("main.productsText")}</p>
              <a className="products__viewAll" href="/shop">
                {t("main.viewAll")}
              </a>
            </div>

            {loadingProducts && (
              <div
                className="products__loading"
                role="status"
                aria-live="polite"
              >
                <span className="spinner" aria-hidden="true"></span>
                <span className="sr-only">
                  {t("common.loading") || "Loading..."}
                </span>
              </div>
            )}

            {!loadingProducts && products.length === 0 && (
              <div className="products__empty">{t("main.empty")}</div>
            )}

            <div className="products__desktop">
              {products.length > 4 && (
                <button
                  className="prod-arrow left"
                  aria-label={t("main.aria.previous")}
                  onClick={() =>
                    setDesktopPage(
                      (p) =>
                        (p - 1 + Math.ceil(products.length / 4)) %
                        Math.ceil(products.length / 4)
                    )
                  }
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path>
                  </svg>
                </button>
              )}

              <div className="products__grid">
                {Array.from({ length: Math.min(4, products.length) }).map(
                  (_, k) => {
                    const itemsPerPage = 4;
                    const base =
                      (desktopPage * itemsPerPage + k) % products.length;
                    const p = products[base];
                    return (
                      <div
                        className="product-card"
                        key={`desk-${base}`}
                        onClick={() => navigateToDetail(p.id, p.title)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigateToDetail(p.id, p.title);
                          }
                        }}
                      >
                        <div className="product-card__image">
                          <img alt={p.title} src={p.image} />
                        </div>
                        <div className="product-card__body">
                          <div className="product-card__title">{p.title}</div>
                         
                          <div
                            className="product-card__divider"
                            aria-hidden="true"
                          />
                          <div className="product-card__footer">
                            <div className="product-card__fromPrice">
                              {p.priceLine}
                            </div>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                            <a
                              className="product-card__btn"
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigateToDetail(p.id, p.title);
                              }}
                            >
                              {t("main.buttons.view")}
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>

              {products.length > 4 && (
                <>
                  <button
                    className="prod-arrow right"
                    aria-label={t("main.aria.next")}
                    onClick={() =>
                      setDesktopPage(
                        (p) => (p + 1) % Math.ceil(products.length / 4)
                      )
                    }
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z"></path>
                    </svg>
                  </button>
                  <div className="prod-dots desktop">
                    {Array.from({
                      length: Math.ceil(products.length / 4),
                    }).map((_, i) => (
                      <button
                        key={`ddot-${i}`}
                        className={`dot${i === desktopPage ? " active" : ""}`}
                        aria-label={t("main.aria.goPage", { n: i + 1 })}
                        onClick={() => setDesktopPage(i)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="reasons" aria-label={t("main.aria.reasons")}>
          <div className="reasons__container">
            <h2 className="reasons__title">{t("main.reasonsTitleAccent")}</h2>
            <div className="reasons__texts">
              {reasonsParagraphs.map((para, idx) => (
                <p className="reasons__text" key={`reasons-text-${idx}`}>
                  {para}
                </p>
              ))}

              <div className="reasons__grid">
                {t("main.reasons", { returnObjects: true }).map((r, idx) => (
                  <div className="reason" key={`reason-${idx}`}>
                    <div className="reason__header">
                      <div className="reason__icon">
                        <ReasonIcon idx={idx} />
                      </div>
                      <div className="reason__title">{r.title}</div>
                    </div>
                    <div className="reason__desc">{r.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="telegram" aria-label={t("main.aria.telegram")}>
          <div className="telegram__container">
            <div className="telegram__content">
              <h2 className="telegram__title">{t("main.telegram.title")}</h2>
              <p className="telegram__text">{t("main.telegram.text")}</p>
              <a
                className="telegram__btn"
                href={telegram}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="telegram__btnInner">
                  <span className="telegram__btnIcon" aria-hidden="true">
                    <svg
                      class="w-6 h-6"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"></path>
                    </svg>
                  </span>
                  <span className="telegram__btnLabel">
                    {t("main.telegram.btn")}
                  </span>
                </span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const ReasonIcon = ({ idx }) => {
  const k = idx % 4;
  if (k === 0)
    return (
      <svg
        className="w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 text-[var(--color-cta)]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        />
      </svg>
    );
  if (k === 1)
    return (
      <svg
        className="w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 text-[var(--color-cta)]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
        />
      </svg>
    );
  if (k === 2)
    return (
      <svg
        className="w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 text-[var(--color-cta)]"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    );
  return (
    <svg
      className="w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 text-[var(--color-cta)]"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
};

export default Main;
